// Group membership changes with a confirmation plan, an audit trail and undo.
//
// A device's group lives only in mdm/devices/<mac>/group, so before this file a bulk "move to group" overwrote
// every selected device's previous group with no way back (2026-10: the whole fleet ended up in one group by one
// click). Every change now goes through applyGroupChange(), which writes the new groups AND an audit entry under
// mdm/group_audit/<id> in the same atomic update: who, when, and for each device its previous and new group.
// revertGroupChange() uses that entry to put devices back.

import { ref, update, push, get, query, limitToLast } from 'firebase/database';
import { getAuth } from 'firebase/auth';

export const AUDIT_PATH = 'mdm/group_audit';

/** Moves above this size (or the whole fleet) must be confirmed by typing the device count. */
export const LARGE_MOVE_THRESHOLD = 20;

const groupOf = (device) => device?.group || '';

/**
 * What a move of `deviceIds` into `target` ('' = no group) would do, without writing anything:
 * - moving: devices whose group changes, with their current group;
 * - unchanged: devices already in the target;
 * - ungrouped: devices that have no group today;
 * - fromOtherGroups: { groupKey: [ids] } for devices that sit in a DIFFERENT group today.
 */
export function planGroupChange(devices, deviceIds, target) {
  const plan = { target: target || '', moving: [], unchanged: [], ungrouped: [], fromOtherGroups: {} };
  deviceIds.forEach((id) => {
    const from = groupOf(devices[id]);
    if (from === plan.target) {
      plan.unchanged.push(id);
      return;
    }
    plan.moving.push({ id, from });
    if (!from) plan.ungrouped.push(id);
    else (plan.fromOtherGroups[from] = plan.fromOtherGroups[from] || []).push(id);
  });
  return plan;
}

function actor() {
  const user = getAuth().currentUser;
  return user ? (user.email || user.uid) : 'unknown';
}

/**
 * Writes the new group for `moves` ([{id, from}]) and the audit entry in ONE update, so a change never lands
 * without its history. Returns { auditId, changes }.
 */
export async function applyGroupChange(db, moves, target, { source = 'devices', action, revertOf } = {}) {
  const to = target || '';
  const auditRef = push(ref(db, AUDIT_PATH));
  const changes = {};
  const updates = {};
  moves.forEach(({ id, from }) => {
    updates[`mdm/devices/${id}/group`] = to || null;
    changes[id] = { from: from || '', to };
  });
  updates[`${AUDIT_PATH}/${auditRef.key}`] = {
    at: Date.now(),
    by: actor(),
    action: action || (to ? 'assign' : 'remove'),
    target: to,
    source,
    count: moves.length,
    changes,
    ...(revertOf ? { revertOf } : {}),
  };
  if (revertOf) updates[`${AUDIT_PATH}/${revertOf}/revertedAt`] = Date.now();
  await update(ref(db), updates);
  return { auditId: auditRef.key, changes };
}

/**
 * Puts the devices of one audit entry back into their previous group. A device is skipped when its group was
 * changed again after that entry (its current group is no longer the entry's "to"), so an undo never overwrites
 * a later, deliberate change. Returns { restored, skipped }.
 */
export async function revertGroupChange(db, auditId) {
  const snap = await get(ref(db, `${AUDIT_PATH}/${auditId}`));
  const entry = snap.val();
  if (!entry || !entry.changes) throw new Error('Change not found');
  if (entry.revertedAt) throw new Error('This change was already reverted');

  const ids = Object.keys(entry.changes);
  const current = await Promise.all(ids.map((id) => get(ref(db, `mdm/devices/${id}/group`))));
  const moves = [];
  let skipped = 0;
  ids.forEach((id, i) => {
    const now = current[i].val() || '';
    const { from, to } = entry.changes[id];
    if (now !== (to || '')) {
      skipped += 1;
      return;
    }
    moves.push({ id, from: now, to: from || '' });
  });

  // Each device goes back to its OWN previous group, so write per device (targets differ).
  const auditRef = push(ref(db, AUDIT_PATH));
  const updates = {};
  const changes = {};
  moves.forEach(({ id, from, to }) => {
    updates[`mdm/devices/${id}/group`] = to || null;
    changes[id] = { from, to };
  });
  updates[`${AUDIT_PATH}/${auditRef.key}`] = {
    at: Date.now(),
    by: actor(),
    action: 'revert',
    target: '',
    source: 'revert',
    count: moves.length,
    changes,
    revertOf: auditId,
  };
  updates[`${AUDIT_PATH}/${auditId}/revertedAt`] = Date.now();
  await update(ref(db), updates);
  return { restored: moves.length, skipped };
}

/** Latest audit entries, newest first. */
export async function loadGroupHistory(db, limit = 30) {
  const snap = await get(query(ref(db, AUDIT_PATH), limitToLast(limit)));
  const val = snap.val() || {};
  return Object.entries(val)
    .map(([id, e]) => ({ id, ...e }))
    .sort((a, b) => (b.at || 0) - (a.at || 0));
}
